/* eslint-disable react-hooks/exhaustive-deps */
import React, {
	useState,
	useMemo,
	useEffect,
	useRef,
	useCallback,
	useContext,
} from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useDrop } from 'react-dnd';
import { memoize } from 'lodash';
import { isEqual } from 'underscore';
import Socket from '../../connection/socket';
import { ConnectedUser } from '../../stores/types/connection';
import { VideoStream } from '../types/videos';
import {
	getFullAssetURI,
	toMap,
	numberToPercent,
	percMultiply,
	getVideoRect,
	isVideo,
	getFeedStyle,
	safeIntFromPx,
} from '../../utils/utils';
import { FIXED_LAYOUTS, PLAIN_LAYOUTS } from '../../utils/video-layout';
import { GreenroomState as GS } from '../../stores/types/types';
import type { Shot, ShotFeed } from '../../stores/types/shots';
import { FEED_TYPES } from '../../stores/reducers/shots';
import FeedPlaceholder from './feed-placeholder';
import FeedMedia from './feed-media';
import FeedPresenter from './feed-presenter';
import FeedEmail from './feed-email';
import FeedScreenShare from './feed-screenshare';
import VideoButtons from './video-buttons';
import Title from './title';
import CustomGraphicsCanvas, { CanvasLocation } from '../custom-graphics/custom-graphics-canvas';
import OverlayDropArea from '../custom-graphics/overlay-drop-area';
import { videoGridClasses } from '../../types/constants';
import useShots from '../../contexts/shots/provider';
import type { DragItem, DropAreaData, DropCallbackData } from '../../stores/types/user-interface';
import { DRAG_TYPE } from '../../stores/reducers/user-interface';
import { showAlert } from '../general-components/alert-service';
import { ALERT_TYPES } from '../general-components/alert-types';
import AudioOnlyFeeds from './audio-only-feeds';
import type {
	MediaItem,
	PresentedMediaItem,
	VideoState,
} from '../../stores/types/media';
import { setShowbuilderMedia as reduxSetShowbuilderMedia } from '../../stores/actions/media';
import TitleDropArea from '../custom-graphics/title-drop-area';
import { setCurrentGraphicsId } from '../../stores/actions/graphics';
import { MediaControlsDataSetState, useGetAllMediaControls, MediaControlsContext } from '../../contexts/media-controls';
import * as keyListeners from '../../utils/keyboard-handler';
import { videoActionCommands } from '../../utils/video-machine';
import { getFileExtension } from '../../utils/media';
import usePrevious from '../../utils/use-previous';
import FeedSipCall from './feed-sip-call';
import FeedSrtStream from './srt-stream/feed-srt-stream';
import type { OnsiteLocation, SrtStream } from '../../stores/types/video';
import FeedOnsiteLocation from './onsite-location/feed-onsite-location';
import { graphicIdByPresenterFeedIndex } from '../../hooks/content/use-drag-detect-feed';
import { LoadChipset, LoadChipsetDraft } from '../../stores/actions/content-chips';
import { areContentMapsEqual, buildContentFromFeeds, processContentItems, ResultItem } from '../../utils/content';
import { setSelectedMediaTemplate } from '../../stores/actions/generate-content';
import { useMediaControlsSync } from '../../hooks/use-media-controls-sync';
import { EnablerStates } from '../../stores/types/session';
import { setEnablerState } from '../../stores/actions/meeting';
import FeedThumbnail from '../general-components/feed-thumbnail';
import FeedIngressStream from './ingress-stream/feed-ingress-stream';

interface OwnProps {
	isForShotEditorModal: boolean;
	iterateLoadedMediaCount: Function;
	socket: Socket | null;
	shotBeingEdited: Shot;
	setShotModalStylepackDrawer?: (stylepackDrawer: any) => void;
}

const ENABLER_THUMBNAIL_TYPES: string[] = [
	FEED_TYPES.EMPTY,
	FEED_TYPES.PRESENTER,
	FEED_TYPES.SIP_CALL,
	FEED_TYPES.EMAIL,
	FEED_TYPES.SCREENSHARE,
	FEED_TYPES.SRT_STREAM,
	FEED_TYPES.INGRESS_STREAM,
	FEED_TYPES.ONSITE_LOCATION,
];

export default function MainVideoEditing(props: OwnProps) {
	const {
		isForShotEditorModal,
		iterateLoadedMediaCount,
		shotBeingEdited,
		socket,
		setShotModalStylepackDrawer,
	} = props;

	const host = useSelector((s: GS) => s.SessionReducer.host);
	const connectedPresenterVideos = useSelector((s: GS) => s.VideoReducer.connectedPresenterVideos);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const typeOfDragging = useSelector((s: GS) => s.UserInterfaceReducer.typeOfDragging);
	const globalIsDragging = useSelector((s: GS) => s.UserInterfaceReducer.globalIsDragging);
	const meetingMedia = useSelector((s: GS) => s.MediaReducer.media);
	const shotModalMedia = useSelector((s: GS) => s.MediaReducer.shotModalMedia);
	const canvasStyle = useSelector((s: GS) => s.ShotsReducer.canvasStyle);
	const currentGraphicId = useSelector((s: GS) => s.GraphicsReducer.currentGraphicId);
	const graphics = useSelector((s: GS) => s.GraphicsReducer.graphics);
	const mediaModalOpen = useSelector((s: GS) => s.MediaReducer.mediaModalOpen);
	const modalOpen = useSelector((s: GS) => s.ModalReducer.modalOpen);
	const srtStreams = useSelector((s: GS) => s.VideoReducer.srtStreams);
	const onsiteLocations = useSelector((s: GS) => s.VideoReducer.onsiteLocations);
	const onsiteLocationStreams = useSelector((s: GS) => s.VideoReducer.onsiteLocationStreams);
	const room = useSelector((s: GS) => s.SessionReducer.meeting?.meeting);
	const channel = useSelector((s: GS) => s.SessionReducer.meeting?.channel);
	const generatedContent = useSelector((s: GS) => s.GeneratedContentReducer.generatedContent);
	const enablerState = useSelector((s: GS) => s.MeetingReducer.enablerState);

	const dispatch = useDispatch();

	const {
		feedDroppedOnCanvas,
		overlayDroppedOnCanvas,
		titleDroppedOnCanvas,
		setFeedExpanded,
	} = useShots();
	const allMediaControls = useGetAllMediaControls();
	const { batchUpdateMediaControls } = useContext(MediaControlsContext);

	// Memoize allMediaControls based on control IDs for use in the marking effect
	// 	This prevents infinite loops.
	// 	A change from control.send -> morphs the underlying object that keeps videoMachineState -> triggers change in the control object -> change in allMediaControls -> triggers something here or in use-media-controls-sync -> may trigger another control.send (esp if it is the init when trying to set states) -> goes back to the beginning of this and goes into an inifinte loop
	//	So because the sorted ids don't change, use it as the dependency watch.
	const allMediaControlsStable = useMemo(() => allMediaControls, [
		Object.keys(allMediaControls).sort().join(',')
	]);
	const controlIds = useMemo(() =>
		Object.keys(allMediaControlsStable).sort().join(','),
		[allMediaControlsStable]
	);

	// Get immediate drag state from react-dnd to avoid Redux update delay
	const [{ itemType }] = useDrop(() => ({
		accept: [DRAG_TYPE.TITLE, DRAG_TYPE.OVERLAY],
		collect: (monitor) => {
			return ({
			isDragging: monitor.canDrop() && monitor.isOver(),
			itemType: monitor.getItemType()
		})}
	}), []);

	const {
		backgroundColor,
		backgroundType, // image, video, or color. Video not yet implemented
		wallpaperColor, // color in hex
		backgroundImage,
		backgroundScale, // a percentage to fill
		// center, inside, or outside (i'm not sure this is possible tbh since
		// it would just be a css border prop)
		presenterVideoBorderPosition,
		layoutSpacingPresets,
	} = canvasStyle;

	const {
		shot_id: currentShotId,
		shot_data: {
			forcedLayout,
			shotFeeds,
			shotGraphics,
		},
		shot_style: {
			applied: {
				topSpacing,
				bottomSpacing,
				sideSpacing,
				innerSpacing,
				spacingLinked,
				gap,
				layout,
				layoutSpacingPresets: shotLayoutSpacingPresets
			},
		},
	} = shotBeingEdited;

	const {
		PLAY,
		PAUSE,
		TIMECHANGE,
	} = videoActionCommands;

	const media = useMemo(
		() => (isForShotEditorModal ? shotModalMedia : meetingMedia),
		[isForShotEditorModal, shotModalMedia, meetingMedia],
	);
	// Sync media control state changes (play/pause/mute/volume/scrub) across all media items
	useMediaControlsSync({ allMediaControls: allMediaControls, media });

	// Mark all media controls as being managed by sync on mount, unmark on unmount
	const markedControlsRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		const updates: Array<{ id: string; state: any }> = [];

		Object.entries(allMediaControlsStable).forEach(([key, control]) => {
			if (!control?.id ||
				control.state?.isUseMediaControlsSyncEnabled || //Already marked
				markedControlsRef.current.has(control.id) || //Already added
				isForShotEditorModal && key.indexOf('-draft') < 0 //in shot editor and key is not a draft
			) {
				return;
			}
			
			markedControlsRef.current.add(control.id);
			updates.push({
				id: control.id,
				state: { ...control.state, isUseMediaControlsSyncEnabled: true },
			});
		});

		if (updates.length > 0) {
			batchUpdateMediaControls(updates);
		}
	}, [controlIds]);

	useEffect(() => {
		// On unmount: unmark all tracked controls
		return () => {
			const cleanupUpdates = Array.from(markedControlsRef.current).map(id => ({
				id,
				state: { isUseMediaControlsSyncEnabled: false },
			}));

			if (cleanupUpdates.length > 0) {
				batchUpdateMediaControls(cleanupUpdates);
			}
			markedControlsRef.current.clear();
		};
	}, []);

	const [videoWidthHeight, setVideoWidthHeight] = useState<any>(null);
	const videoWrapperRef = useRef<any>(null);
	const lastDroppedItemRef = useRef<DragItem | null>(null);
	const prevGraphicsByPresenterFeed = useRef<Record<number, number | null> | null>(null);
	const prevShotId = useRef<number | null>(null);
	const [showbuilderMedia, setShowbuilderMedia] = useState<PresentedMediaItem[]>([]);
	const [showAudioOnlyFeedsButton, setShowAudioOnlyFeedsButton] = useState(false);
	const [justDropped, setJustDropped] = useState(false);

	const standardFeeds = useMemo(() => (shotFeeds?.length > 0
		? shotFeeds.filter((shotFeed) => !shotFeed.audioOnly)
		: []), [shotFeeds]);
	const audioOnlyFeeds = useMemo(() => (shotFeeds?.length > 0
		? shotFeeds.filter((shotFeed) => shotFeed.audioOnly)
		: []), [shotFeeds]);

	const resizeObserver = new ResizeObserver((entries) => {
		// 177%, 56.25%
		const wrapWidth = entries[0].contentRect.width;
		const wrapHeight = entries[0].contentRect.height;

		const styleObject = getVideoRect(wrapWidth, wrapHeight);

		setVideoWidthHeight(styleObject);
	});

	useEffect(() => {
		setShowAudioOnlyFeedsButton(audioOnlyFeeds.length > 0);
	}, [audioOnlyFeeds]);

	const allGraphics = useMemo((): number[] => [
		...(shotGraphics || []),
		...graphics.filter((g) => !!g.is_global).map((g) => g.id ?? 0),
	], [shotGraphics, graphics]);
	const prevAllGraphics = usePrevious(allGraphics) ?? [];

	useEffect(() => {
		if (!isForShotEditorModal && currentGraphicId && !isEqual(allGraphics, prevAllGraphics)) {
			const inShotGraphics = allGraphics.includes(currentGraphicId);
			if (!inShotGraphics) {
				// when shot changes clear the currently selected graphic
				dispatch(setCurrentGraphicsId(0));
			}
		}
	}, [allGraphics, prevAllGraphics, isForShotEditorModal]);

	useEffect(() => {
		if (videoWrapperRef.current) { resizeObserver.observe(videoWrapperRef.current); }

		return () => {
			if (videoWrapperRef.current) resizeObserver.disconnect();
		};
	}, [videoWrapperRef.current]);

	const handleProcessingContentItems = useCallback(async (
		content: ResultItem[],
		channel: number,
		meetingId: number,
	) => {
		const contentItemsIds = await processContentItems(content, { channel, meetingId }, isForShotEditorModal);
		if(!contentItemsIds.length) return;
		contentItemsIds.forEach((contentId) => {
			dispatch(isForShotEditorModal
				? LoadChipsetDraft(meetingId, contentId)
				: LoadChipset(channel, contentId)
			);
		})
	}, [dispatch, isForShotEditorModal]);
	
	useEffect(() => {
		if (!shotBeingEdited || !channel || !room || !generatedContent.length) return;

		// Only execute if currentShot hasn't changed,
		// it means do not regenerate content on shot change
		if (prevShotId.current && prevShotId.current !== shotBeingEdited.shot_id) {
			prevShotId.current = shotBeingEdited.shot_id;
			return;
		}
		prevShotId.current = shotBeingEdited.shot_id;

		const graphicsByPresenterFeeds = graphicIdByPresenterFeedIndex(shotBeingEdited, isForShotEditorModal);
		if (!graphicsByPresenterFeeds) return;

		if (areContentMapsEqual(prevGraphicsByPresenterFeed.current, graphicsByPresenterFeeds)) {
			return;
		}
		prevGraphicsByPresenterFeed.current = graphicsByPresenterFeeds;
		const content = buildContentFromFeeds(graphicsByPresenterFeeds, generatedContent);

		processContentItems(content, { channel, meetingId: room }, isForShotEditorModal)
			.then((results) => {
				(results ?? []).forEach((contentId) => {
					dispatch(isForShotEditorModal
						? LoadChipsetDraft(room, contentId)
						: LoadChipset(channel, contentId)
					);
				})
			}).catch((e) => { console.error(e) });
	}, [
		forcedLayout,
		layoutSpacingPresets,
		topSpacing,
		bottomSpacing,
		sideSpacing,
		innerSpacing,
		spacingLinked,
		gap,
		layout,
		shotLayoutSpacingPresets,
		shotBeingEdited,
		channel,
		room,
		generatedContent.length
	]);

	const backgroundStyle: any = useMemo(() => {
		const newStyle: any = {};
		newStyle.backgroundColor = backgroundColor;
		if (backgroundType === 'image') {
			newStyle.backgroundSize = `${backgroundScale}%`;
			newStyle.backgroundImage = `url('${getFullAssetURI(backgroundImage)}')`;
		}
		newStyle.display = 'grid';
		return newStyle;
	}, [backgroundColor, backgroundImage, backgroundScale, backgroundType]);

	const marginMemo = useMemo(() => {
		const fixedSpacingLayouts = [...FIXED_LAYOUTS, ...PLAIN_LAYOUTS];
		const isFixedLayout = forcedLayout
			? fixedSpacingLayouts.some((layout) => forcedLayout.includes(layout))
			: false;

		if (shotBeingEdited && isFixedLayout) {
			return {
				columnGap: 0,
				rowGap: 0,
				paddingTop: 0,
				paddingBottom: 0,
				paddingRight: 0,
				paddingLeft: 0,
			};
		} if (shotBeingEdited) {
			const layoutMap = toMap('layout', layoutSpacingPresets);
			// if no layout exists set no margins
			const layout = layoutMap.get(forcedLayout);
			const backup = {
				spacing: {
					top: 0, bottom: 0, side: 0, inner: 0,
				},
			};
			const margins = layout?.spacing ? layout : backup;
			const {
				inner, top, side, bottom,
			} = margins.spacing;
			return {
				columnGap: numberToPercent(inner),
				rowGap: percMultiply(numberToPercent(inner) || '0%'),
				paddingTop: numberToPercent(top),
				paddingBottom: numberToPercent(bottom),
				paddingRight: numberToPercent(side),
				paddingLeft: numberToPercent(side),
			};
		}

		return {
			columnGap: 0,
			rowGap: 0,
			paddingTop: 0,
			paddingBottom: 0,
			paddingRight: 0,
			paddingLeft: 0,
		};
	}, [forcedLayout, layoutSpacingPresets]);

	const getVidStreamFromId = (id: number) => {
		const feed = connectedPresenterVideos.find((vid: VideoStream) => vid.profile === id);
		return feed;
	};

	const getVidStreamFromStreamId = (id: string) => {
		const feed = connectedPresenterVideos.find((vid: VideoStream) => vid.id === id);
		return feed;
	};

	const getScreenShareFromEmail = (email: string) => {
		const connUser = connectedUsers.find(
			(connectedUser: ConnectedUser) => (
				connectedUser.email === email && connectedUser.screenShareWebrtcId
			),
		);
		return connectedPresenterVideos.find(
			(vid: VideoStream) => vid.id === connUser?.screenShareWebrtcId,
		);
	};

	const getSrtStreamFromId = (id: number) => {
		const feed = srtStreams.find((vid: SrtStream) => vid.id === id);
		return feed;
	};

	const getOnsiteLocationFromId = (id: number) => ({
		data: onsiteLocations.find((vid: OnsiteLocation) => vid.id === id),
		stream: onsiteLocationStreams.get(id)?.[0],
	});

	const hasPictureInPictureMemo = useMemo(
		() => shotBeingEdited.shot_data.forcedLayout.includes('picture-in-picture'),
		[forcedLayout],
	);

	const numberOfVideosOnCanvas = () => {
		const vidNumber = shotBeingEdited.shot_data.shotFeeds.reduce(
			(previousValue:number, currentValue:ShotFeed) => {
				const singleMedia = media.find((elm) => elm.id === currentValue.media);
				let videoCheck = false;
				if (singleMedia) {
					videoCheck = isVideo(singleMedia.type);
				}
				return previousValue + (videoCheck ? 1 : 0);
			},
			0,
		);
		return vidNumber;
	};

	// we resolve drops in this component because it's the common ancestor
	// of the feed components, so we can pass down the function to call on drop
	const feedDrop = async (item: DragItem, dropAreaData?: DropCallbackData) => {
		// Prevent dropping a lower as a feed media, instead add it as a lower drop.
		if (typeOfDragging === DRAG_TYPE.TITLE || item.type === DRAG_TYPE.TITLE) {
			if (!dropAreaData) return;
			titleDrop(item, dropAreaData);
			return;
		}
		const mediaBeingReplacedId = shotBeingEdited.shot_data.shotFeeds.find(
			(shot) => shot.position === dropAreaData?.feedPosition,
		)?.media;
		const mediaItemBeingReplaced = media.find((mediaItem) => mediaItem.id === mediaBeingReplacedId);
		const replacingItem = media.find((mediaItem) => mediaItem.id === item.id);
		const beingReplacedIsAVideo = isVideo(mediaItemBeingReplaced?.type || '');
		const replacingItemIsVideo = isVideo(
			item.template && typeof item.id === 'string'
				? getFileExtension(item.id)
				: replacingItem?.type || '',
		);
		const videoIsInCanvas = shotFeeds.some(
			(shot) => shot.media === replacingItem?.id,
		);

		const canDropItem = replacingItemIsVideo
			? (numberOfVideosOnCanvas() < 8)
				|| (numberOfVideosOnCanvas() === 8 && beingReplacedIsAVideo) || videoIsInCanvas
			: true;
		if (typeof dropAreaData?.feedPosition === 'number' && canDropItem) {
			await feedDroppedOnCanvas(item, socket, isForShotEditorModal, dropAreaData.feedPosition);
			lastDroppedItemRef.current = item;
			setJustDropped(true);
		} else {
			showAlert({
				type: ALERT_TYPES.WARNING,
				message: 'Video Capacity Reached',
				description: 'Only 8 videos are allowed on the canvas at a given time',
				duration: 5000,
			});
		}
	};

	const overlayDrop = useCallback((item: DragItem, dropData: DropCallbackData) => {
		overlayDroppedOnCanvas(item, socket, isForShotEditorModal, dropData);
	}, [overlayDroppedOnCanvas, socket, isForShotEditorModal]);

	const titleDrop = useCallback((item: DragItem, dropData: DropCallbackData) => {
		titleDroppedOnCanvas(item, socket, isForShotEditorModal, dropData);
	}, [titleDroppedOnCanvas, socket, isForShotEditorModal]);

	useEffect(() => {
		const shotMediaIds = shotBeingEdited.shot_data.shotFeeds
			.filter((feed: ShotFeed) => feed.feedType === FEED_TYPES.MEDIA)
			.map((feed: ShotFeed) => feed.media);

		const updatedShowbuilderMedia = showbuilderMedia.map((item) => {
			// This resets the video state in the showbuilderMedia array
			// if the media item is not in the current shot
			if (!shotMediaIds.includes(item.mediaId)) {
				return {
					...item,
					videoState: {
						paused: true,
						timeElapsed: 0,
						volume: 1,
					},
				};
			}

			// This carries over the elapsed value for a new shot
			// if the same media item is playing in a different feed position
			if (item.videoState?.timeStarted && !item.videoState?.paused) {
				const timeStart = item.videoState?.timeStarted.valueOf();
				const newTimeElapsed = ((new Date().valueOf() - timeStart) / 1000);
				return {
					...item,
					videoState: {
						...item.videoState,
						timeElapsed: newTimeElapsed + (item.videoState.timeElapsed ?? 0),
						timeStarted: new Date(),
					},
				};
			}
			return item;
		});

		if (!isEqual(showbuilderMedia, updatedShowbuilderMedia)) {
			setShowbuilderMedia(updatedShowbuilderMedia);
		}

		for (const feed of shotBeingEdited.shot_data.shotFeeds) {
			if (feed.media && feed.feedType === FEED_TYPES.MEDIA) {
				const mediaItem = media.find(m => m.id === feed.media);
				if (mediaItem?.provider && mediaItem?.template_type) {
					dispatch(setSelectedMediaTemplate(mediaItem, mediaItem.template_type));
					return;
				}
			}
		}
		
		dispatch(setSelectedMediaTemplate(null, ''));
	}, [shotBeingEdited]);

	// TODO: This is not an ideal way of handling state (sending local to redux)
	// A better solution is needed, but this will make it work for now
	useEffect(() => {
		dispatch(reduxSetShowbuilderMedia(showbuilderMedia));
	}, [showbuilderMedia]);

	const updateShowbuilderMedia = (itemUpdate: PresentedMediaItem, videoUpdate?: VideoState) => {
		setShowbuilderMedia((updatedShowbuilderMedia) => updatedShowbuilderMedia.map(
			(showboardMediaItem: PresentedMediaItem) => {
				if (showboardMediaItem.mediaId === itemUpdate.mediaId) {
					if (videoUpdate) {
						return {
							...showboardMediaItem,
							videoState: {
								...showboardMediaItem.videoState as VideoState,
								...videoUpdate,
							},
						};
					}
					return itemUpdate;
				}
				return showboardMediaItem;
			},
		));
	};

	const getShowbuilderMediaItem = memoize((mediaId: number) => (
		showbuilderMedia.find((med: PresentedMediaItem) => med.mediaId === mediaId)
	));

	// If video, rerenders the media feed component, to reset the video machine references.
	// If image, to avoid long blank render between slides by not changing key.
	const getKeyForMediaFeed = memoize((id: number): string => {
		const presMediaItem = showbuilderMedia.find((med: PresentedMediaItem) => med.mediaId === id);
		const medItem = media.find((m) => m.id === id);
		if (presMediaItem && medItem) {
			const page = medItem?.media_data.image_array[presMediaItem.currentPage - 1];
			if (page && isVideo(page)) {
				return `${presMediaItem.currentPage}-${id}`;
			}
		}
		return `1-${id}`;
	});

	// const allVideosArePaused = useMemo((): boolean => (
	// 	Object.values(allMediaControls).every(({ state: paused }) => paused)
	// ), [allMediaControls.state.paused]);

	const setVideosPlaying = (
		mediaControls: MediaControlsDataSetState,
		isModalOpen: boolean = false,
	): void => {
		const options = {
			doFireSync: true,
			from: 'space bar key listener',
		};

		if (isModalOpen || document.getElementById('modal-large-overlay')) {
			return;
		}

		const allVideosArePaused = Object.values(mediaControls).every((m) => m?.state?.paused);

		Object.values(mediaControls).forEach(({ send }) => {
			if (allVideosArePaused) {
				send?.(PLAY, options);
			} else {
				send?.(PAUSE, options);
			}
		});
	};

	useEffect(() => {
		const isModalOpen = mediaModalOpen || modalOpen;

		keyListeners.addListener(keyListeners.KEYS.KEY_SPACE, () => {
			setVideosPlaying(allMediaControls, isModalOpen);
		});
	}, [allMediaControls, mediaModalOpen, modalOpen]);

	useEffect(() => {
		if (!justDropped) return;

		requestAnimationFrame(async () => {
			setJustDropped(false);
			const itemId = lastDroppedItemRef.current?.id;
			if (!itemId || !channel || !room) return;
			const updatedPresenterFeeds = graphicIdByPresenterFeedIndex(shotBeingEdited, isForShotEditorModal);
			let content = buildContentFromFeeds(updatedPresenterFeeds, generatedContent);
			// Filter out the content feed that has not been updated
			content = content.filter((contentItem) => contentItem.userId === itemId);
			const contentItemsIds = await processContentItems(content, { channel, meetingId: room }, isForShotEditorModal);
			if(!contentItemsIds.length) return;
			contentItemsIds.forEach((contentId) => {
				dispatch(isForShotEditorModal
					? LoadChipsetDraft(room, contentId)
					: LoadChipset(channel, contentId)
				);
			})
		});
	}, [justDropped]);

	function videosInCanvas(
		selectedShotFeeds: ShotFeed[],
		startAt: number = 0,
		audioOnly: boolean = false,
	) {
		return (
			<>
				{selectedShotFeeds && selectedShotFeeds.map((shotFeedRef: ShotFeed, mapIndex: number) => {
					const shotFeed = { ...shotFeedRef };
					const index = startAt + mapIndex;

					// if a user was put into a shot before creating an account and joins the room while
					// the shot is on the canvas, change the feed from email to presenter to put them in
					// the canvas without editing the shot and forcing it to rerender
					if (shotFeed.feedType === FEED_TYPES.EMAIL && shotFeed.email) {
						const connUser = connectedUsers.find(
							(connectedUser: ConnectedUser) => connectedUser.email === shotFeed.email,
						);
						if (connUser && connUser.gr_user) {
							shotFeed.feedType = FEED_TYPES.PRESENTER;
							shotFeed.presenter = connUser.gr_user;
							shotFeed.email = null;
						}
					}

					if (
						(shotFeed.feedType === FEED_TYPES.MEDIA || shotFeed.feedType === FEED_TYPES.MEDIA_AUDIO)
						&& shotFeed.media
						&& !getShowbuilderMediaItem(shotFeed.media)
					) {
						const med = media.find((m: MediaItem) => m.id === shotFeed.media);
						if (med) {
							const newItem = {
								mediaId: med?.id ?? 1,
								currentPage: 1,
								pages: med?.media_data.image_array ?? [],
								name: med?.custom_name ?? med?.original_name ?? '',
								videoState: isForShotEditorModal ? {
									muted: true, paused: true, volume: 1, timeElapsed: 0,
								} : {
									muted: false, paused: true, volume: 1, timeElapsed: 0,
								},
							};
							setShowbuilderMedia([...showbuilderMedia, newItem]);
						}
					}

					const { style, insideStyle } = getFeedStyle(
						forcedLayout,
						index,
						canvasStyle,
					);

					if (enablerState !== EnablerStates.None && ENABLER_THUMBNAIL_TYPES.includes(shotFeed.feedType)) {
						return (
							<React.Fragment key={mapIndex}>
								<div 
									key={mapIndex} 
									className={classNames('video-container-presenter', videoGridClasses[index])}
									style={style}
								>
									{presenterVideoBorderPosition === 'inside' && <div style={insideStyle} /> }
									<FeedThumbnail shotFeed={shotFeed} />
									{(shotFeed.presenter || shotFeed.onsiteLocationId) && (
										<Title
											key={currentShotId}
											idOfPresenter={shotFeed.presenter}
											onsiteLocation={shotFeed.onsiteLocationId
												? onsiteLocations.find((l) => l.id === shotFeed.onsiteLocationId)
												: undefined
											}
										/>
									)}
								</div>
							</React.Fragment>
						);
					}

					return (
						<React.Fragment key={mapIndex}>
							<div key={mapIndex} className={classNames('video-container-presenter', videoGridClasses[index])}
								style={style}>
								{shotFeed.feedType === FEED_TYPES.EMPTY && (
									<FeedPlaceholder
										feedPosition={index}
										dropCallback={feedDrop}
									/>
								)}
								{
								// eslint-disable-next-line max-len
								(shotFeed.feedType === FEED_TYPES.MEDIA || shotFeed.feedType === FEED_TYPES.MEDIA_AUDIO)
								&& shotFeed.media && (
									<FeedMedia
										key={getKeyForMediaFeed(shotFeed.media)}
										feedPosition={index}
										mediaId={shotFeed.media}
										socket={socket}
										isForMainVideoEditingComponent={true}
										iterateLoadedMediaCount={iterateLoadedMediaCount}
										isEditingFeed
										isForShotEditorModal={isForShotEditorModal}
										dropCallback={feedDrop}
										audioOnly={audioOnly}
										showbuilderMediaItem={getShowbuilderMediaItem(shotFeed.media)}
										updateShowbuilderMedia={updateShowbuilderMedia}
										setShotModalStylepackDrawer={setShotModalStylepackDrawer}
									/>
								)}
								{shotFeed.feedType === FEED_TYPES.PRESENTER && shotFeed.presenter && (
									<FeedPresenter
										feedPosition={index}
										video={getVidStreamFromId(shotFeed.presenter)}
										grUser={shotFeed.presenter}
										dropCallback={feedDrop}
									/>
								)}
								{shotFeed.feedType === FEED_TYPES.SIP_CALL && shotFeed.sipStreamId && (
									<FeedSipCall
										feedPosition={index}
										video={getVidStreamFromStreamId(shotFeed.sipStreamId)}
										dropCallback={feedDrop}
										audioOnly={audioOnly}
										isForShotEditorModal={isForShotEditorModal}
										expanded={shotFeed.expanded}
										toggleExpandedCallback={() => {
											if (socket) {
												setFeedExpanded(shotFeed, socket, !shotFeed.expanded, isForShotEditorModal);
											}
										}}
									/>
								)}
								{shotFeed.feedType === FEED_TYPES.EMAIL && shotFeed.email && (
									<FeedEmail
										feedPosition={index}
										email={shotFeed.email}
										dropCallback={feedDrop}
									/>
								)}
								{shotFeed.feedType === FEED_TYPES.SCREENSHARE && shotFeed.screenshare && (
									<FeedScreenShare
										feedPosition={index}
										video={getScreenShareFromEmail(shotFeed.screenshare)}
										email={shotFeed.screenshare}
										dropCallback={feedDrop}
									/>
								)}
								{shotFeed.feedType === FEED_TYPES.SRT_STREAM && shotFeed.srtStreamId && (
									<FeedSrtStream
										feedPosition={index}
										srtStream={getSrtStreamFromId(shotFeed.srtStreamId)}
										audioOnly={audioOnly}
										dropCallback={feedDrop}
									/>
								)}
								{shotFeed.feedType === FEED_TYPES.INGRESS_STREAM && shotFeed.ingressStreamId && (
									<FeedIngressStream
										feedPosition={index}
										ingressStreamId={shotFeed.ingressStreamId}
										dropCallback={feedDrop}
									/>
								)}
								{shotFeed.feedType === FEED_TYPES.ONSITE_LOCATION && shotFeed.onsiteLocationId && (
									<FeedOnsiteLocation
										feedPosition={index}
										onsiteLocation={getOnsiteLocationFromId(shotFeed.onsiteLocationId)}
										isForShotEditorModal={isForShotEditorModal}
										dropCallback={feedDrop}
									/>
								)}
								{/* this was added because when there was no feed like screenshare
								//stopped the inside border did not show */}
								{presenterVideoBorderPosition === 'inside' && (
									<div style={insideStyle} />
								)}
								{(shotFeed.presenter || shotFeed.onsiteLocationId) && (
									<Title
										key={currentShotId}
										idOfPresenter={shotFeed.presenter}
										onsiteLocation={shotFeed.onsiteLocationId
											? onsiteLocations.find((l) => l.id === shotFeed.onsiteLocationId)
											: undefined
										}
									/>
								)}
							</div>
						</React.Fragment>
					);
				})}
			</>
		);
	}

	function handleEnablerTimeChange(timeChange: number): void {
		Object.values(allMediaControls).forEach((control) => {
			if (control?.state?.video) {
				const currentElapsed = control.state.elapsed;
				if (currentElapsed !== undefined && control.send) {
					control.send(
						TIMECHANGE,
						{ 
							currentTime: currentElapsed + timeChange,
							duration: control.state.duration,
							doFireSync: true,
							from: 'handleEnablerTimeChange',
						},
					);
				}
			}
		});
	}

	function handleEnablerPlayPause() {
		const shouldPlay = enablerState !== EnablerStates.Playing;
		dispatch(setEnablerState(shouldPlay ? EnablerStates.Playing : EnablerStates.Paused));
		Object.values(allMediaControls).forEach((control) => {
			if (control?.state?.video && control.send) {
				if (shouldPlay) {
					control.send(
						PLAY,
						{ doFireSync: true, from: 'handleEnablerPlayPause' },
					);
				} else {
					control.send(
						PAUSE,
						{ doFireSync: true, from: 'handleEnablerPlayPause' },
					);
				}
			}
		});
	}

	return (
		<>
			<div id="video-wrapper" ref={videoWrapperRef} className={classNames(
				'include-audio-only-feeds-edit',
				{ embed, enabler: enablerState !== EnablerStates.None },
			)}>
				{shotBeingEdited && (
					<div style={{ ...backgroundStyle, ...videoWidthHeight }} className="main-video-container">
						{enablerState !== EnablerStates.None && (
							<div 
								style={{
									position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
									display: 'grid',
									gridTemplateColumns: 'repeat(3, 1fr)',
									pointerEvents: 'auto',
								}}
							>
								<div onClick={() => handleEnablerTimeChange(-10)} style={{ zIndex: 1000 }}/>
								<div onClick={handleEnablerPlayPause} style={{ zIndex: 1000 }} />
								<div onClick={() => handleEnablerTimeChange(10)} style={{ zIndex: 1000 }} />
							</div>
						)}
						{videoWidthHeight
							&& <CustomGraphicsCanvas
								renderedGraphics={shotGraphics}
								canEdit={true}
								height={safeIntFromPx(videoWidthHeight.height)}
								width={safeIntFromPx(videoWidthHeight.width)}
								shot={shotBeingEdited}
								isForShotEditorModal={isForShotEditorModal}
								socket={socket}
								location={isForShotEditorModal ? CanvasLocation.ShotEditor : ''}
							/>
						}
						{hasPictureInPictureMemo ? (
							<>
								{!!audioOnlyFeeds.length && (
									<div
										style={marginMemo}
										className={classNames(
											'main-video hidden',
											shotBeingEdited.shot_data.forcedLayout,
											{ backgroundColor: wallpaperColor },
										)}
									>
										{videosInCanvas(audioOnlyFeeds, 0, true)}
									</div>
								)}
								<div
									id={isForShotEditorModal ? 'main-video-view-current-modal' : 'main-video-view-current'}
									style={marginMemo}
									className={classNames(
										'main-video layer1',
										shotBeingEdited.shot_data.forcedLayout,
										{ backgroundColor: wallpaperColor },
									)}
								>
									{videosInCanvas(standardFeeds.slice(1), 1)}
								</div>
								<div
									id={isForShotEditorModal ? 'main-video-view-current-pip-modal' : 'main-video-view-current-pip'}
									className={classNames(
										'main-video layer2',
										shotBeingEdited.shot_data.forcedLayout,
										{ backgroundColor: wallpaperColor },
									)}
								>
									{videosInCanvas(standardFeeds.slice(0, 1))}
								</div>
							</>
						) : (
							<>
								{!!audioOnlyFeeds.length && (
									<div
										style={marginMemo}
										className={classNames(
											'main-video hidden',
											shotBeingEdited.shot_data.forcedLayout,
											{ backgroundColor: wallpaperColor },
										)}
									>
										{videosInCanvas(audioOnlyFeeds, 0, true)}
									</div>
								)}
								<div
									style={marginMemo}
									id={isForShotEditorModal ? 'main-video-view-current-modal' : 'main-video-view-current'}
									className={classNames(
										'main-video',
										shotBeingEdited.shot_data.forcedLayout,
										{ backgroundColor: wallpaperColor },
									)}
								>
									{videosInCanvas(standardFeeds)}
								</div>
							</>
						)}
						<div
							id="controls-container-editing"
							data-testid="controls-container-editing"
							className={classNames('main-video', shotBeingEdited.shot_data.forcedLayout)}
							style={{ ...marginMemo, pointerEvents: globalIsDragging ? 'none' : 'inherit' }}
						/>
						{(itemType === DRAG_TYPE.OVERLAY)
							&& <OverlayDropArea dropCallback={overlayDrop} />
						}
						{(itemType === DRAG_TYPE.TITLE)
							&& <TitleDropArea dropCallback={titleDrop} />
						}
						{!embed && !host && (
							<div className="left-panel-below-video-setup">
								<div className={classNames('lower-setup-button-container', !showAudioOnlyFeedsButton && 'hide-audio-only-button')}>
									{showAudioOnlyFeedsButton && (
										<AudioOnlyFeeds
											isForShotEditorModal={false}
											socket={socket}
										/>
									)}
									{!isForShotEditorModal && (
										<div id="portal-unmute-snackbar-2" />
									)}
								</div>
							</div>
						)}
						<div className="left-panel-below-video-setup">
							<VideoButtons
								socket={socket}
								shotBeingEdited={shotBeingEdited}
								isForShotEditorModal={isForShotEditorModal}
								// treated as 0th indexed feed
								feedPosition={0}
								dropCallback={feedDrop}
								/>
						</div>
					</div>
				)}
			</div>
			<div id="feed-media-context-menu-portal" />
			<div id="layout-modal-portal" />
			<div id="video-hover-options-portal" />
		</>
	);
}
